import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, Teleport as _Teleport, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "filters" }
const _hoisted_2 = { class: "external-id-wrapper" }
const _hoisted_3 = {
  key: 0,
  class: "assignment-label"
}
const _hoisted_4 = ["src"]
const _hoisted_5 = { class: "plate-overlay" }
const _hoisted_6 = { class: "plate-number" }
const _hoisted_7 = { class: "battery-col" }
const _hoisted_8 = { class: "battery-label" }

import { computed, onMounted, watch, ref } from 'vue'
import { useRouter } from 'vue-router'

import { PaginationParams } from '@/models/api'
import { EvCarProfile, EvCarProfileStatus } from '@/models/evCarProfile'
import { getAssignmentLabel } from './Details/helpers'

import { GetEvCarsParams, useEvCarsStore } from '@/store/ev-vehicles'

import { routeNames } from '@/router/route-names'
import useQueryParams from '@/composables/useQueryParams'

import CommonError from '@/components/CommonError.vue'
import Loader from '@/components/Loader.vue'
import { commonCase } from '@/helpers/strings'


export default /*@__PURE__*/_defineComponent({
  __name: 'List',
  props: {
    query: {}
  },
  setup(__props: any) {

const props = __props

const evCarsStore = useEvCarsStore()
const router = useRouter()

const isLoading = computed(() => evCarsStore.listModule.isLoading)
const isInitialLoadingDone = computed(
  () => evCarsStore.listModule.isInitialLoadingDone
)
const error = computed(() => evCarsStore.listModule.error)
const list = computed(() => evCarsStore.listModule.list)
const meta = computed(() => evCarsStore.listModule.meta)

const sortedList = computed(() => {
  const items = list.value || []
  return [...items].sort((a, b) =>
    (a.plate_number || '').localeCompare(b.plate_number || '', undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  )
})

// const filter = ref<EvCarProfileStatus | undefined>(EvCarProfileStatus.ACTIVE);
const filter = ref<EvCarProfileStatus | undefined>(undefined)
const isAvailable = ref<boolean | undefined>(undefined)
const externalIdFilter = ref<string>('')

const windowHeight = ref(window.innerHeight)

const getEvCars = (params: PaginationParams & GetEvCarsParams) => {
  evCarsStore.getEvCars(params)
}

const params = useQueryParams<PaginationParams & GetEvCarsParams>({
  ...props.query,
})

onMounted(() => {
  window.addEventListener('resize', () => {
    windowHeight.value = window.innerHeight
  })
  if (params.status) {
    filter.value = params.status as EvCarProfileStatus
  }
  if (params.is_available !== undefined) {
    isAvailable.value = Boolean(params.is_available)
  }
  if (params.external_id) {
    externalIdFilter.value = params.external_id
  }

  getEvCars({
    ...params,
    status: filter.value,
    is_available: isAvailable.value,
    external_id: externalIdFilter.value,
  })
})

watch(
  () => props.query,
  updated => {
    if (updated.status) {
      filter.value = updated.status as EvCarProfileStatus
    } else {
      filter.value = undefined
    }

    if (updated.is_available !== undefined) {
      isAvailable.value = Boolean(updated.is_available)
    } else {
      isAvailable.value = undefined
    }

    const newExternalId = updated.external_id || ''
    if (newExternalId !== externalIdFilter.value) {
      externalIdFilter.value = newExternalId
    }

    Object.assign(params, updated)
  },
  { deep: true }
)

watch(params, updatedParams => {
  // If external_id is present in params, ensure it's synced to local state
  // and other filters are cleared if needed
  if (updatedParams.external_id) {
    if (externalIdFilter.value !== updatedParams.external_id) {
      externalIdFilter.value = updatedParams.external_id
    }
    if (filter.value !== undefined) filter.value = undefined
    if (isAvailable.value !== undefined) isAvailable.value = undefined
  }

  getEvCars({
    ...updatedParams,
    status: filter.value,
    is_available: isAvailable.value,
    external_id: externalIdFilter.value,
  })
})

watch([filter, isAvailable], ([updatedFilter, updatedIsAvailable]) => {
  // Only update params if these values actually changed and are not undefined due to external_id search
  if (updatedFilter !== undefined || updatedIsAvailable !== undefined) {
    Object.assign(params, {
      status: updatedFilter,
      is_available: updatedIsAvailable,
      external_id: undefined, // Clear external_id when status filters are used
    })
    externalIdFilter.value = ''
  } else if (!externalIdFilter.value) {
    // If everything is cleared (e.g. "All EV Cars" clicked), update params
    Object.assign(params, {
      status: undefined,
      is_available: undefined,
    })
  }
})

const applyExternalIdFilter = () => {
  if (externalIdFilter.value) {
    // Only reset other filters if they are set
    if (filter.value !== undefined) filter.value = undefined
    if (isAvailable.value !== undefined) isAvailable.value = undefined

    // Explicitly set params to trigger the watch(params) and API call
    // We set status/is_available to undefined in params as well
    Object.assign(params, {
      external_id: externalIdFilter.value,
      status: undefined,
      is_available: undefined,
    })
  } else {
    // If input is empty, just clear the external_id param
    Object.assign(params, {
      external_id: undefined,
    })
  }
}

const onRetryPress = () => {
  getEvCars({
    ...params,
    status: filter.value,
    is_available: isAvailable.value,
    external_id: externalIdFilter.value,
  })
}

const setFilter = (newFilter: EvCarProfileStatus | undefined) => {
  filter.value = newFilter
  isAvailable.value = undefined
  externalIdFilter.value = ''
  applyExternalIdFilter()
}

const setFreeCarsFilter = () => {
  filter.value = EvCarProfileStatus.ACTIVE
  isAvailable.value = true
  externalIdFilter.value = ''
  applyExternalIdFilter()
}

// const handleRowClick = (car: EvCarProfile) => {
//   router.push({ name: routeNames.evVehicleDetails, params: { id: car.id } });
// };
const handleRowClick = (car: EvCarProfile) => {
  router.push({ name: routeNames.evVehicleDetails, params: { id: car.id } })
}

const handleAddVehicle = () => {
  router.push({ name: routeNames.createEvCar })
}

const hoverImage = ref<string | undefined>(undefined)
const hoverPlateNumber = ref<string | undefined>(undefined)
const hoverBatteryPercent = ref<number | null>(null)
const hoverPosition = ref({ x: 0, y: 0 })

const batteryColors = [
  { color: '#f56c6c', percentage: 20 },
  { color: '#e6a23c', percentage: 40 },
  { color: '#67c23a', percentage: 100 },
]

const getBatteryPercent = (car: EvCarProfile): number | null => {
  const raw = car.electric_level ?? car.battery_details?.level
  if (raw === undefined || raw === null) return null
  const value = Number(raw)
  return Number.isFinite(value)
    ? Math.min(100, Math.max(0, Math.round(value)))
    : null
}

const handleCellMouseEnter = (
  row: EvCarProfile,
  column: any,
  cell: any,
  event: MouseEvent
) => {
  if (row.images && row.images.length > 0) {
    hoverImage.value = row.images[0].secure_url
  } else {
    hoverImage.value = undefined
  }
  hoverPlateNumber.value = row.plate_number || 'No plate'
  hoverBatteryPercent.value = getBatteryPercent(row)
}

const handleTableMouseLeave = () => {
  hoverImage.value = undefined
  hoverPlateNumber.value = undefined
  hoverBatteryPercent.value = null
}

const handleMouseMove = (event: MouseEvent) => {
  hoverPosition.value = { x: event.clientX, y: event.clientY }
}

const getStatusClass = (status: string) => {
  switch (status) {
    case EvCarProfileStatus.ACTIVE:
      return 'status-active'
    case EvCarProfileStatus.ADMIN_ACTIVE:
      return 'status-admin-active'
    case EvCarProfileStatus.INACTIVE:
      return 'status-inactive'
    case EvCarProfileStatus.INCOMPLETE:
      return 'status-incomplete'
    case EvCarProfileStatus.ADMIN_BLOCKED:
      return 'status-admin-blocked'
    default:
      return ''
  }
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_scrollbar = _resolveComponent("el-scrollbar")!
  const _component_el_progress = _resolveComponent("el-progress")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (error.value)
      ? (_openBlock(), _createBlock(CommonError, {
          key: 0,
          errorMessage: error.value,
          onRetryPress: onRetryPress
        }, null, 8, ["errorMessage"]))
      : (!isInitialLoadingDone.value || !meta.value)
        ? (_openBlock(), _createBlock(Loader, { key: 1 }))
        : (_openBlock(), _createBlock(_component_el_scrollbar, {
            key: 2,
            always: ""
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_el_col, { span: 24 }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_1, [
                        _createVNode(_component_el_button, {
                          class: "btn",
                          "native-type": "button",
                          type: 
              filter.value === undefined && !externalIdFilter.value ? 'primary' : 'default'
            ,
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (setFilter(undefined)))
                        }, {
                          default: _withCtx(() => _cache[7] || (_cache[7] = [
                            _createTextVNode(" All EV Cars ")
                          ])),
                          _: 1,
                          __: [7]
                        }, 8, ["type"]),
                        _createVNode(_component_el_button, {
                          class: "btn",
                          type: 
              filter.value === _unref(EvCarProfileStatus).ACTIVE && !isAvailable.value
                ? 'success'
                : 'default'
            ,
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (setFilter(_unref(EvCarProfileStatus).ACTIVE)))
                        }, {
                          default: _withCtx(() => _cache[8] || (_cache[8] = [
                            _createTextVNode(" Active ")
                          ])),
                          _: 1,
                          __: [8]
                        }, 8, ["type"]),
                        _createVNode(_component_el_button, {
                          class: "btn",
                          type: 
              filter.value === _unref(EvCarProfileStatus).INACTIVE ? 'danger' : 'default'
            ,
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (setFilter(_unref(EvCarProfileStatus).INACTIVE)))
                        }, {
                          default: _withCtx(() => _cache[9] || (_cache[9] = [
                            _createTextVNode(" Inactive ")
                          ])),
                          _: 1,
                          __: [9]
                        }, 8, ["type"]),
                        _createVNode(_component_el_button, {
                          class: "btn",
                          type: 
              filter.value === _unref(EvCarProfileStatus).INCOMPLETE ? 'warning' : 'default'
            ,
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (setFilter(_unref(EvCarProfileStatus).INCOMPLETE)))
                        }, {
                          default: _withCtx(() => _cache[10] || (_cache[10] = [
                            _createTextVNode(" Incomplete ")
                          ])),
                          _: 1,
                          __: [10]
                        }, 8, ["type"]),
                        _createVNode(_component_el_button, {
                          class: "btn",
                          type: 
              filter.value === _unref(EvCarProfileStatus).ACTIVE && isAvailable.value
                ? 'primary'
                : 'default'
            ,
                          onClick: setFreeCarsFilter
                        }, {
                          default: _withCtx(() => _cache[11] || (_cache[11] = [
                            _createTextVNode(" Free Cars ")
                          ])),
                          _: 1,
                          __: [11]
                        }, 8, ["type"]),
                        _createElementVNode("div", _hoisted_2, [
                          _createVNode(_component_el_input, {
                            modelValue: externalIdFilter.value,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((externalIdFilter).value = $event)),
                            placeholder: "id in system Geotek",
                            class: "external-id-input",
                            clearable: "",
                            onKeydown: _withKeys(_withModifiers(applyExternalIdFilter, ["prevent","stop"]), ["enter"])
                          }, {
                            append: _withCtx(() => [
                              _createVNode(_component_el_button, {
                                icon: "el-icon-search",
                                onClick: applyExternalIdFilter
                              })
                            ]),
                            _: 1
                          }, 8, ["modelValue", "onKeydown"])
                        ]),
                        _createVNode(_component_el_button, {
                          type: "success",
                          class: "add-vehicle-btn",
                          onClick: handleAddVehicle
                        }, {
                          default: _withCtx(() => _cache[12] || (_cache[12] = [
                            _createElementVNode("i", { class: "el-icon-plus" }, null, -1),
                            _createTextVNode(" Add Vehicle ")
                          ])),
                          _: 1,
                          __: [12]
                        })
                      ]),
                      _withDirectives((_openBlock(), _createBlock(_component_el_table, {
                        style: {"width":"100%"},
                        data: sortedList.value,
                        onRowClick: handleRowClick,
                        onCellMouseEnter: handleCellMouseEnter,
                        onMouseleave: handleTableMouseLeave,
                        onMousemove: handleMouseMove,
                        "element-loading-background": "rgba(255, 255, 255, 0.5)",
                        class: "ev-cars-table",
                        "default-sort": { prop: 'plate_number', order: 'ascending' }
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_table_column, {
                            prop: "plate_number",
                            label: "Plate number",
                            sortable: ""
                          }, {
                            default: _withCtx((scope) => [
                              _createTextVNode(_toDisplayString(scope.row.plate_number || '–'), 1)
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_el_table_column, {
                            prop: "external_id",
                            label: " Id Geotek"
                          }),
                          _createVNode(_component_el_table_column, { label: "Status" }, {
                            default: _withCtx((scope) => [
                              _createElementVNode("span", {
                                class: _normalizeClass(getStatusClass(scope.row.status))
                              }, _toDisplayString(_unref(commonCase)(scope.row.status)), 3),
                              (_unref(getAssignmentLabel)(scope.row))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(getAssignmentLabel)(scope.row)), 1))
                                : _createCommentVNode("", true)
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_el_table_column, {
                            prop: "model_make_id",
                            label: "Make"
                          }),
                          _createVNode(_component_el_table_column, {
                            prop: "model_name",
                            label: "Model"
                          }),
                          _createVNode(_component_el_table_column, {
                            prop: "model_year",
                            label: "Year"
                          }),
                          _createVNode(_component_el_table_column, {
                            prop: "range_per_charge",
                            label: "Range (km)"
                          }),
                          _createVNode(_component_el_table_column, {
                            prop: "battery_capacity",
                            label: "Battery (kWh)"
                          }),
                          _createVNode(_component_el_table_column, {
                            width: "130px",
                            align: "right"
                          }, {
                            default: _withCtx((scope) => [
                              _createVNode(_component_el_button, {
                                plain: "",
                                type: "primary",
                                size: "small",
                                icon: "el-icon-view",
                                onClick: ($event: any) => (handleRowClick(scope.row))
                              }, {
                                default: _withCtx(() => _cache[13] || (_cache[13] = [
                                  _createTextVNode(" Show ")
                                ])),
                                _: 2,
                                __: [13]
                              }, 1032, ["onClick"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["data"])), [
                        [_directive_loading, isLoading.value]
                      ]),
                      _createVNode(_component_el_pagination, {
                        background: "",
                        layout: "total, sizes, prev, pager, next",
                        total: meta.value.total,
                        disabled: isLoading.value,
                        "page-sizes": [10, 20, 50, 100],
                        "page-size": _unref(params).perPage,
                        "onUpdate:pageSize": _cache[5] || (_cache[5] = ($event: any) => ((_unref(params).perPage) = $event)),
                        "current-page": _unref(params).page,
                        "onUpdate:currentPage": _cache[6] || (_cache[6] = ($event: any) => ((_unref(params).page) = $event)),
                        "popper-class": "ev-pagination-popper"
                      }, null, 8, ["total", "disabled", "page-size", "current-page"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (hoverImage.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            style: _normalizeStyle({
        position: 'fixed',
        // Якщо курсор у нижній половині екрана, показуємо вікно ЗВЕРХУ від курсору
        top:
          hoverPosition.value.y > windowHeight.value / 2
            ? hoverPosition.value.y - 220 + 'px'
            : hoverPosition.value.y + 20 + 'px',
        left: hoverPosition.value.x + 20 + 'px',
        zIndex: 9999,
        pointerEvents: 'none',
        width: '300px',
        height: '220px',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        background: '#fff',
      })
          }, [
            _createElementVNode("img", {
              src: hoverImage.value,
              style: {"width":"100%","height":"180px","object-fit":"cover","display":"block"}
            }, null, 8, _hoisted_4),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("span", _hoisted_6, _toDisplayString(hoverPlateNumber.value), 1),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("span", _hoisted_8, _toDisplayString(hoverBatteryPercent.value !== null ? hoverBatteryPercent.value + '%' : '–'), 1),
                (hoverBatteryPercent.value !== null)
                  ? (_openBlock(), _createBlock(_component_el_progress, {
                      key: 0,
                      percentage: hoverBatteryPercent.value,
                      "show-text": false,
                      "stroke-width": 6,
                      color: batteryColors,
                      style: {"width":"60px","margin-top":"2px"}
                    }, null, 8, ["percentage"]))
                  : _createCommentVNode("", true)
              ])
            ])
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})